import { useEffect, useMemo, useState } from 'react';

import type { ChatMessage } from '@/components/Widget';
import type { PersonsMap } from '@/hooks/usePersonsMap';
import { useMessages } from '@/store/messages/messages.store';

interface UseCommentsSectionProps {
  entityId: string;
  personsMap: PersonsMap;
  commentsCount: number;
  initiallyExpanded?: boolean;
  fetchOnMount?: boolean;
  /**
   * The caller has already put this thread in the store, so there is nothing to fetch
   * on mount — the task card loads it before it opens.
   */
  preloaded?: boolean;
}

export const useCommentsSection = ({
  entityId,
  personsMap,
  commentsCount,
  initiallyExpanded = false,
  fetchOnMount = false,
  preloaded = false,
}: UseCommentsSectionProps) => {
  const messages = useMessages((state) => state.messagesByRoom[entityId] ?? null);
  const [isHidden, setIsHidden] = useState(!initiallyExpanded);
  const [hasFetchedInitial, setHasFetchedInitial] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const { getMessages, sendMessage } = useMessages();

  const displayMessages = useMemo<ChatMessage[]>(() => {
    return (
      messages?.map((msg) => {
        const person = personsMap.get(msg.creator_id);
        const senderName = person
          ? `${person.person_info.first_name} ${person.person_info.last_name}`.trim()
          : 'Unknown';
        return {
          id: msg.id,
          sender: senderName,
          role: '',
          message: msg.text,
          unread: msg.status === 'created',
          createdAt: msg.created_at,
          updatedAt: msg.updated_at,
          creatorId: msg.creator_id,
          roomId: msg.room_id,
        };
      }) ?? []
    );
  }, [messages, personsMap]);

  useEffect(() => {
    if (preloaded) return;
    if ((initiallyExpanded || fetchOnMount) && !hasFetchedInitial && entityId) {
      void getMessages(entityId).finally(() => setHasFetchedInitial(true));
    }
  }, [preloaded, initiallyExpanded, fetchOnMount, hasFetchedInitial, entityId, getMessages]);

  const handleToggleComments = () => {
    if (isHidden) {
      void getMessages(entityId);
    }
    setIsHidden(!isHidden);
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || isSending) return;

    setIsSending(true);
    try {
      // sendMessage refetches the thread into the per-room cache itself.
      await sendMessage(entityId, messageInput);
      setMessageInput('');
      setIsHidden(false);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void handleSendMessage();
    }
  };

  const displayCount = messages?.length ?? commentsCount;
  const hasComments = displayCount > 0;

  const refetchMessages = async () => {
    await getMessages(entityId);
  };

  return {
    messages,
    displayMessages,
    isHidden,
    messageInput,
    setMessageInput,
    handleToggleComments,
    handleSendMessage,
    handleKeyDown,
    hasComments,
    displayCount,
    isSending,
    refetchMessages,
  };
};
